import { CompatClient, Stomp } from '@stomp/stompjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client';

import { getTermLocateByMdn } from 'services/api-service/center';

interface UseWebSocketMessagesOptions {
  onError?: (error: Error) => void;
  reconnectDelay?: number;
  enableDebug?: boolean;
}

interface Vessel {
  mdn: string;
  giIdx: string;
  [key: string]: any;
}

export const useWebSocketMessages = (
  vessels: Vessel[],
  roomId: string | null | undefined,
  curRoom: any,
  roomInfo: any,
  options: UseWebSocketMessagesOptions = {}
) => {
  const { onError, reconnectDelay = 10000, enableDebug = process.env.NODE_ENV !== 'production' } = options;

  const client = useRef<CompatClient>();
  const [messages, setMessages] = useState<any[]>([]);
  const [boats, setBoats] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const [giIdx, setGiIdx] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [targetRoom, setTargetRoom] = useState<any>(null);

  const formatDateTime = (date, time) => {
    return `${date} ${time}:00`;
  };

  const getLocationAndFindGiIdx = useCallback(async (targetRoom: any, vessels: any[]) => {
    if (!targetRoom?.mdn || targetRoom.mdn === '') {
      setGiIdx(undefined);
      setMessages([]);
      setBoats([]);
      return;
    }

    setIsLoading(true);

    try {
      const formatted = formatDateTime(targetRoom.date ?? targetRoom.accDate, targetRoom.time ?? targetRoom.accTime);
      const response: any = await getTermLocateByMdn(targetRoom.mdn, formatted, 10);

      if (response?.data?.log && Array.isArray(response.data.log)) {
        if (response.data.log.length > 1) {
          setMessages(response.data.log);
        } else {
          setMessages([]);
        }
      } else {
        setMessages([]);
      }
      if (response?.data?.rLogs && Array.isArray(response.data.rLogs)) {
        if (response.data.rLogs.length > 1) {
          setBoats(response.data.rLogs);
        } else {
          setBoats([]);
        }
      } else {
        setBoats([]);
      }

      if (!targetRoom.isExpire) {
        const index = vessels.findIndex((vessel) => vessel.mdn === targetRoom.mdn);
        const foundGiIdx = index !== -1 ? vessels[index].bbc : undefined;

        if (foundGiIdx) {
          // setGiIdx(foundGiIdx);
          setGiIdx('3');
        } else {
          setGiIdx(undefined);
        }
      } else {
        setGiIdx(undefined);
      }
    } catch (error) {
      setGiIdx(undefined);
      setMessages([]);
      setBoats([]);
      if (onError) {
        onError(error as Error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateMessage = useCallback(
    (message: any) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        if (parsedMessage.mdn === targetRoom.mdn) {
          setMessages((prev) => [...prev, parsedMessage]);
        }
        // console.log('New Message Received :', parsedMessage);
      } catch (error) {
        if (onError) {
          onError(error as Error);
        }
      }
    },
    [onError]
  );

  const connect = useCallback(() => {
    if (!giIdx) {
      return;
    }

    try {
      client.current = Stomp.over(() => {
        const sock = new SockJS('https://smartsar.kr/backend/ws');
        return sock;
      });

      client.current.reconnectDelay = reconnectDelay;

      if (!enableDebug) {
        client.current.debug = () => {
          // 프로덕션에서는 디버그 로그 비활성화
        };
      }

      client.current.connect(
        {},
        () => {
          setIsConnected(true);
          setConnectionError(null);

          client.current?.subscribe(`/sub/group/${giIdx}`, updateMessage, {
            // 필요한 경우 헤더 추가
          });
        },
        (error: any) => {
          setIsConnected(false);
          setConnectionError(error);
          if (onError) {
            onError(error);
          }
        }
      );
    } catch (error) {
      setConnectionError(error as Error);
      if (onError) {
        onError(error as Error);
      }
    }
  }, [giIdx]);

  const disconnect = useCallback(() => {
    if (client.current?.connected) {
      client.current.disconnect();
    }
    setMessages([]);
    setBoats([]);
    setIsConnected(false);
    setConnectionError(null);
  }, []);

  const sendMessage = useCallback((destination: string, message: any, headers?: any) => {
    if (client.current?.connected) {
      client.current.send(destination, headers || {}, JSON.stringify(message));
      // console.log(`Send Message: ${destination}`, message);
    } else {
      console.warn('Not connected WebSocket.');
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  useEffect(() => {
    if (roomId != undefined && curRoom) {
      setTargetRoom(curRoom);
    } else if (roomInfo) {
      setTargetRoom(roomInfo);
    } else {
      setTargetRoom(null);
    }
  }, [roomInfo]);

  useEffect(() => {
    if (targetRoom && targetRoom.type === '') {
      getLocationAndFindGiIdx(targetRoom, vessels);
    }
  }, [targetRoom, getLocationAndFindGiIdx]);

  useEffect(() => {
    if (giIdx === null || giIdx === undefined) {
      disconnect();
    } else {
      if (!targetRoom.isExpire && roomId !== undefined) {
        connect();
      }
    }
  }, [giIdx, connect, disconnect]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    messages,
    boats,
    isConnected,
    connectionError,
    giIdx,
    isLoading,
    sendMessage,
    clearMessages,
    disconnect,
    reconnect: connect,
    refreshLocation: getLocationAndFindGiIdx,
  };
};
